// Festival Map View - Interactive map with vendor directory
import React, { useState, useEffect, useRef } from 'react';
import { Search, Star, ChevronLeft, MapPin, Filter, List, Map as MapIcon } from 'lucide-react';
import { Festival, Vendor, SAMPLE_VENDORS } from '../data/festivals';

interface FestivalMapViewProps {
  festival: Festival;
  vendors?: Vendor[];
  recommendedVendorIds?: string[];
  onSelectVendor: (vendor: Vendor) => void;
  onBack: () => void;
}

export const FestivalMapView: React.FC<FestivalMapViewProps> = ({
  festival,
  vendors = SAMPLE_VENDORS.filter(v => v.festivalId === festival.id),
  recommendedVendorIds = [],
  onSelectVendor,
  onBack,
}) => {
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map');
  const [searchQuery, setSearchQuery] = useState('');
  const [showRecommendedOnly, setShowRecommendedOnly] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);

  // Filter vendors
  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vendor.brands.some(b => b.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilter = !showRecommendedOnly || recommendedVendorIds.includes(vendor.id);
    return matchesSearch && matchesFilter;
  });

  // Initialize Leaflet map
  useEffect(() => {
    const L = (window as any).L;
    if (!L || !mapContainerRef.current || mapRef.current) return;

    // Create map centered on festival location
    const map = L.map(mapContainerRef.current, {
      zoomControl: false, // We'll add custom position
    }).setView([festival.mapCenter.lat, festival.mapCenter.lng], festival.mapZoom);

    // Add zoom control to top-right
    L.control.zoom({ position: 'topright' }).addTo(map);

    // Add dark-themed tile layer (CartoDB Dark Matter - free, no API key)
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 20
    }).addTo(map);

    // Create custom marker icons
    const createMarkerIcon = (isRecommended: boolean) => {
      return L.divIcon({
        className: 'custom-marker',
        html: `
          <div style="
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: ${isRecommended ? '#f59e0b' : 'rgba(255,255,255,0.8)'};
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            transition: transform 0.2s;
          ">
            ${isRecommended
              ? '<svg width="16" height="16" viewBox="0 0 20 20" fill="#1c1917"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>'
              : '<svg width="16" height="16" viewBox="0 0 20 20" fill="#44403c"><path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/></svg>'
            }
          </div>
        `,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      });
    };

    // Add vendor markers
    vendors.forEach(vendor => {
      if (vendor.location) {
        const isRecommended = recommendedVendorIds.includes(vendor.id);
        const marker = L.marker(
          [vendor.location.lat, vendor.location.lng],
          { icon: createMarkerIcon(isRecommended) }
        ).addTo(map);

        marker.on('click', () => setSelectedVendor(vendor));
        markersRef.current.push(marker);
      }
    });

    mapRef.current = map;

    // Invalidate size after a short delay to ensure proper rendering
    setTimeout(() => {
      map.invalidateSize();
    }, 100);

    return () => {
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, [festival, vendors, recommendedVendorIds]);

  // Pan to selected vendor
  useEffect(() => {
    if (mapRef.current && selectedVendor?.location) {
      mapRef.current.panTo([selectedVendor.location.lat, selectedVendor.location.lng]);
    }
  }, [selectedVendor]);

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-stone-900/90 backdrop-blur-lg border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <button onClick={onBack} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <span className="text-white font-semibold">{festival.shortName}</span>
          </div>
          <nav className="flex items-center gap-4 text-sm">
            <button className="text-white/50 hover:text-white">Home</button>
            <button className="text-white/50 hover:text-white">Recommendations</button>
            <button className="text-amber-500 font-medium">Festival Map</button>
            <button className="text-white/50 hover:text-white">My Profile</button>
          </nav>
          <button className="px-4 py-1.5 rounded-lg bg-amber-500 text-stone-900 text-sm font-medium hover:bg-amber-400">
            Login
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Map */}
        <div className={`relative ${viewMode === 'map' ? 'flex-1' : 'hidden md:block md:flex-1'}`}>
          <div ref={mapContainerRef} className="absolute inset-0 z-0" style={{ background: '#1a1a1a' }} />

          {/* Map overlay - search */}
          <div className="absolute top-4 left-4 right-4 md:right-auto md:w-80 z-[1000]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                placeholder="Search vendors or cigar brands"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-stone-800/90 backdrop-blur border border-white/10 text-white text-sm placeholder:text-white/40 focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>

          {/* Map overlay - view toggle (mobile) */}
          <div className="absolute bottom-4 left-4 md:hidden z-[1000]">
            <button
              onClick={() => setViewMode('list')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-stone-800/90 backdrop-blur border border-white/10 text-white text-sm font-medium"
            >
              <List className="w-4 h-4" />
              View List
            </button>
          </div>

          {/* Selected vendor popup */}
          {selectedVendor && (
            <div className="absolute bottom-4 left-4 right-4 md:left-4 md:right-auto md:w-80 z-[1000]">
              <div className="bg-stone-800/95 backdrop-blur rounded-xl border border-white/10 p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-white font-semibold">{selectedVendor.name}</h3>
                    {selectedVendor.boothNumber && (
                      <p className="text-amber-500 text-sm">Booth {selectedVendor.boothNumber}</p>
                    )}
                  </div>
                  {recommendedVendorIds.includes(selectedVendor.id) && (
                    <Star className="w-5 h-5 text-amber-500 fill-current" />
                  )}
                </div>
                {selectedVendor.description && (
                  <p className="text-white/60 text-sm mb-3">{selectedVendor.description}</p>
                )}
                <div className="flex flex-wrap gap-1 mb-3">
                  {selectedVendor.brands.slice(0, 3).map(brand => (
                    <span key={brand} className="px-2 py-0.5 rounded text-xs bg-white/10 text-white/70">
                      {brand}
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => onSelectVendor(selectedVendor)}
                    className="flex-1 py-2 rounded-lg bg-amber-500 text-stone-900 font-medium text-sm hover:bg-amber-400"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => setSelectedVendor(null)}
                    className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm hover:bg-white/20"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Fallback if Leaflet not loaded */}
          {!(window as any).L && (
            <div className="absolute inset-0 flex items-center justify-center bg-stone-800 z-10">
              <div className="text-center p-8">
                <MapIcon className="w-12 h-12 text-white/30 mx-auto mb-4" />
                <p className="text-white/50 mb-2">Map loading...</p>
                <p className="text-white/30 text-sm">
                  Please wait while the map loads
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Vendor Directory sidebar */}
        <div className={`w-full md:w-96 bg-stone-900 border-l border-white/10 overflow-y-auto ${
          viewMode === 'list' ? 'block' : 'hidden md:block'
        }`}>
          {/* Mobile back to map */}
          {viewMode === 'list' && (
            <button
              onClick={() => setViewMode('map')}
              className="md:hidden flex items-center gap-2 p-4 text-amber-500 text-sm font-medium"
            >
              <MapIcon className="w-4 h-4" />
              Back to Map
            </button>
          )}

          <div className="p-4">
            <h2 className="text-lg font-semibold text-white mb-4">Vendor Directory</h2>

            {/* Filters */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setShowRecommendedOnly(!showRecommendedOnly)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  showRecommendedOnly
                    ? 'bg-amber-500 text-stone-900'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <Star className="w-4 h-4" />
                My Recommendations
              </button>
              <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 text-white text-sm font-medium hover:bg-white/20">
                <Filter className="w-4 h-4" />
                Boutique Brands
              </button>
            </div>

            {/* Vendor list */}
            <div className="space-y-2">
              {filteredVendors.map(vendor => (
                <button
                  key={vendor.id}
                  onClick={() => {
                    setSelectedVendor(vendor);
                    if (viewMode === 'list') setViewMode('map');
                  }}
                  className={`w-full flex items-center justify-between p-3 rounded-xl border transition-colors text-left ${
                    selectedVendor?.id === vendor.id
                      ? 'bg-amber-500/20 border-amber-500/50'
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      recommendedVendorIds.includes(vendor.id)
                        ? 'bg-amber-500'
                        : 'bg-white/10'
                    }`}>
                      {recommendedVendorIds.includes(vendor.id) ? (
                        <Star className="w-4 h-4 text-stone-900" />
                      ) : (
                        <MapPin className="w-4 h-4 text-white/60" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-white font-medium text-sm">{vendor.name}</h3>
                      <p className="text-white/50 text-xs">
                        Booth {vendor.boothNumber || 'TBA'}
                      </p>
                    </div>
                  </div>
                  {vendor.featured && (
                    <span className="px-2 py-0.5 rounded text-xs bg-amber-500/20 text-amber-500 font-medium">
                      Featured
                    </span>
                  )}
                </button>
              ))}
            </div>

            {filteredVendors.length === 0 && (
              <div className="text-center py-8 text-white/50">
                <p>No vendors found</p>
                {showRecommendedOnly && (
                  <button
                    onClick={() => setShowRecommendedOnly(false)}
                    className="mt-2 text-amber-500 text-sm"
                  >
                    Show all vendors
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FestivalMapView;
